import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import { clockTime } from '../../shared/lib/format';
import {
  CheckResult,
  usageSteps,
  type TCredential,
  type TCredentialCheck,
} from '../../entities/credential';

const consequence = (credential: TCredential): string => {
  const steps = usageSteps(credential.usage);
  const [only] = steps;
  if (steps.length === 0) return 'No step uses these yet, so nothing fails because of it.';
  if (steps.length === 1 && only) {
    return `The ${only.nodeName} step in ${only.flowName} uses these, so it will fail until the check passes.`;
  }
  return `${steps.length} steps use these, so they will fail until the check passes.`;
};

interface ICheckFailedDialogProps {
  credential: TCredential;
  check: TCredentialCheck;
  timeZone?: string;
  editable: boolean;
  /** "Replace the password", "Replace the bot token"; null when the app is not one of ours. */
  replaceLabel: string | null;
  checking: boolean;
  checkError: unknown;
  onReplace: () => void;
  onCheckAgain: () => void;
  onOpenChange: (open: boolean) => void;
}

/** Board 13, "Saved, but the check failed": kept, and said plainly that it does not work. */
export const CheckFailedDialog = ({
  credential,
  check,
  timeZone,
  editable,
  replaceLabel,
  checking,
  checkError,
  onReplace,
  onCheckAgain,
  onOpenChange,
}: ICheckFailedDialogProps) => (
  <Modal
    open
    onOpenChange={onOpenChange}
    width="md"
    title={credential.name}
    description={`Saved at ${clockTime(credential.updatedAt, timeZone)}.`}
    footer={
      editable ? (
        <>
          {replaceLabel && (
            <Button variant="secondary" size="sm" onClick={onReplace}>
              {replaceLabel}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            waiting={checking}
            waitingLabel="Checking"
            onClick={onCheckAgain}
          >
            Check again
          </Button>
        </>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      )
    }
  >
    <div className="flex flex-col gap-3">
      <CheckResult ok={false} detail={check.detail} />
      <p className="text-data text-muted">{consequence(credential)}</p>
      <MutationError error={checkError} />
    </div>
  </Modal>
);
